import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { can, useMe } from '../../auth.tsx';
import { Button, ErrorBox, Loading } from '../../components/ui.tsx';

export type StructureRecordType =
  | 'department'
  | 'cost_center'
  | 'legal_entity'
  | 'classification'
  | 'management_account'
  | 'business_unit';

interface History {
  netsuiteCodes: { code: string; from: string; until: string | null }[];
  changes: {
    id: string;
    occurredAt: string;
    action: 'INSERT' | 'UPDATE' | 'DELETE';
    table: string;
    changedFields: string[] | null;
    actor: string | null;
  }[];
}

/** Vínculos, códigos do NetSuite (atual e anteriores) e histórico de alterações de um registro. */
export function RecordDetails({ type, id }: { type: StructureRecordType; id: string }) {
  const { t, i18n } = useTranslation();
  const when = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' });
  const history = useQuery({
    queryKey: ['structure', 'history', type, id],
    queryFn: () => api.get<History>(`/structure/history?type=${type}&id=${id}`),
  });
  const usage = useQuery({
    queryKey: ['structure', 'usage', type, id],
    queryFn: () =>
      api.get<{ usage: string; total: number }[]>(`/structure/usage?type=${type}&id=${id}`),
  });
  if (history.isPending || usage.isPending) return <Loading />;
  if (history.isError) return <ErrorBox error={history.error} />;
  if (usage.isError) return <ErrorBox error={usage.error} />;
  return (
    <div className="grid grid-cols-1 gap-4 rounded-md bg-paper p-3 text-sm md:grid-cols-3">
      <div className="flex flex-col gap-1">
        <span className="font-medium">{t('structure.info.usage')}</span>
        {usage.data.length === 0 ? (
          <span className="text-muted">{t('structure.info.unused')}</span>
        ) : (
          usage.data.map((u) => (
            <span key={u.usage}>
              {t(`structure.usage.${u.usage}`)}: {u.total}
            </span>
          ))
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-medium">{t('structure.info.netsuite')}</span>
        {history.data.netsuiteCodes.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          history.data.netsuiteCodes.map((c) => (
            <span key={`${c.code}-${c.from}`}>
              <span className="font-mono">{c.code}</span>{' '}
              <span className="text-xs text-muted">
                {when.format(new Date(c.from))} →{' '}
                {c.until ? when.format(new Date(c.until)) : t('structure.info.current')}
              </span>
            </span>
          ))
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="font-medium">{t('structure.info.history')}</span>
        {history.data.changes.slice(0, 15).map((c) => (
          <span key={c.id} className="text-xs">
            {when.format(new Date(c.occurredAt))} · {c.actor ?? t('audit.system')} ·{' '}
            {t(`structure.info.actions.${c.action}`)}
            {c.table === 'external_reference' && ` (${t('structure.info.netsuite')})`}
            {c.changedFields?.length ? `: ${c.changedFields.join(', ')}` : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Botão "Histórico e vínculos" (só para quem vê a Estrutura Funcional). */
export function InfoButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  if (!can(me, 'structure.catalog.read')) return null;
  return (
    <Button variant="secondary" className="whitespace-nowrap" onClick={onClick}>
      {t(open ? 'structure.info.close' : 'structure.info.open')}
    </Button>
  );
}
